import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { apiClient, configureApiClient, unwrapApiBody } from '@jn/api-client';
import { maxRole } from '../routes';
import { applyWorkspaceThemeFromRow, clearWorkspaceTheme } from '../lib/workspaceTheme';
import { clearRecoCountdownAnchor } from '../lib/recoCountdownAnchor';

// spec §4 / guardrails §3.2: three workspace roles + platform SuperAdmin. No Viewer.
export type Role = 'SuperAdmin' | 'Owner' | 'Admin' | 'Editor';

/** Workspace Owner or Admin (not platform SuperAdmin). */
export function isWorkspaceManager(role: Role | undefined): boolean {
  return role === 'Owner' || role === 'Admin';
}

/** Options for `POST /auth/login`: body `workspaceSlug` and/or header `X-Tenant-Id` (`loginTenantId`). */
export type LoginOptions = {
  /** Sent as `X-Tenant-Id` when set (e.g. owner invite). If a valid UUID, it wins over `workspaceSlug` per API. */
  loginTenantId?: string;
  /** Tenant locator in JSON body. */
  workspaceSlug?: string;
};

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  role: Role;
  workspaceId: string;
  /** SP-027 — from `GET /workspaces` for current tenant (logo + badge in shell). */
  workspace?: {
    logoUrl: string | null;
    brandBadge: string | null;
  };
}

/** Token payload returned by login / invite-accept — used to hydrate AuthContext without a password re-prompt. */
export type SessionTokens = {
  accessToken: string;
  refreshToken?: string | null;
  expiresIn?: number;
  email: string;
  tenantId?: string | null;
  workspaceSlug?: string | null;
};

interface AuthContextValue {
  user: AuthUser | null;
  token: string | null;
  /** False while we still have a refresh token and are minting access / rebuilding `user` (avoid login flash on F5). */
  isAuthReady: boolean;
  isAuthenticated: boolean;
  permissions: string[];
  login: (email: string, password: string, options?: LoginOptions) => Promise<AuthUser>;
  /**
   * Persist tokens + real email into the same storage keys as `login()`.
   * Use after invite-accept (or similar) when the API already issued a session.
   */
  establishSession: (session: SessionTokens) => AuthUser;
  logout: () => Promise<void>;
  refreshToken: () => Promise<string>;
  switchWorkspace: (workspaceId: string) => Promise<void>;
  /** Session timeout warning modal state (AUTH-02). */
  timeoutWarning: {
    show: boolean;
    secondsRemaining: number;
  };
  /** Extend session (called from timeout modal "Stay signed in" button). */
  extendSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const REFRESH_STORAGE_KEY = 'auth_refresh_token';
const USER_EMAIL_STORAGE_KEY = 'auth_user_email';
const TENANT_ID_STORAGE_KEY = 'auth_tenant_id';
/** SuperAdmin UI context only — which workspace to view (Users list, etc.). Must NOT drive X-Tenant-Id. */
const VIEW_TENANT_ID_STORAGE_KEY = 'auth_view_tenant_id';
const WORKSPACE_SLUG_STORAGE_KEY = 'auth_workspace_slug';

function readStoredTenantId(): string | undefined {
  try {
    const s = localStorage.getItem(TENANT_ID_STORAGE_KEY)?.trim();
    return s || undefined;
  } catch {
    return undefined;
  }
}

function readStoredViewTenantId(): string | undefined {
  try {
    const s = localStorage.getItem(VIEW_TENANT_ID_STORAGE_KEY)?.trim();
    return s || undefined;
  } catch {
    return undefined;
  }
}

function writeStoredViewTenantId(tenantId: string): void {
  try {
    localStorage.setItem(VIEW_TENANT_ID_STORAGE_KEY, tenantId);
  } catch {
    /* ignore */
  }
}

function readStoredWorkspaceSlug(): string | undefined {
  try {
    const s = localStorage.getItem(WORKSPACE_SLUG_STORAGE_KEY)?.trim();
    return s || undefined;
  } catch {
    return undefined;
  }
}

function persistTenantContext(tenantId: string, workspaceSlug: string | null | undefined): void {
  try {
    localStorage.setItem(TENANT_ID_STORAGE_KEY, tenantId);
    if (workspaceSlug?.trim()) localStorage.setItem(WORKSPACE_SLUG_STORAGE_KEY, workspaceSlug.trim());
    else localStorage.removeItem(WORKSPACE_SLUG_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

function clearTenantContext(): void {
  try {
    localStorage.removeItem(TENANT_ID_STORAGE_KEY);
    localStorage.removeItem(WORKSPACE_SLUG_STORAGE_KEY);
    localStorage.removeItem(VIEW_TENANT_ID_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * SP-027 — load theme tokens + branding for the active tenant (editors can call `GET /workspaces` for a single row).
 */
async function fetchAndApplyWorkspaceBranding(workspaceId: string): Promise<AuthUser['workspace'] | undefined> {
  if (!workspaceId.trim()) {
    clearWorkspaceTheme();
    return undefined;
  }
  try {
    const { fetchWorkspacesList } = await import('../api/backendV1');
    const { items } = await fetchWorkspacesList({ limit: 100, cacheBust: true });
    const want = workspaceId.trim().toLowerCase();
    const row =
      items.find((i) => i.id.trim().toLowerCase() === want) ??
      (items.length === 1 ? items[0] : undefined);
    if (!row) {
      // Empty/cached 304 body — keep whatever branding is already on the user.
      return undefined;
    }
    applyWorkspaceThemeFromRow(row);
    return { logoUrl: row.logoUrl, brandBadge: row.brandBadge };
  } catch {
    return undefined;
  }
}

/** Best-effort JWT payload parse (no signature verify) — used when login omits `user`. */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const [, b64] = token.split('.');
    if (!b64) return null;
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const json = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** Seconds until JWT `exp`, or null if missing. */
function secondsUntilJwtExp(accessToken: string): number | null {
  const claims = decodeJwtPayload(accessToken);
  if (!claims) return null;
  const exp = Number(claims.exp);
  if (!Number.isFinite(exp)) return null;
  return Math.max(0, exp - Math.floor(Date.now() / 1000));
}

function tenantIdFromEnv(): string | undefined {
  return import.meta.env.VITE_TENANT_ID;
}

function userFromTokenAndOptionalProfile(
  accessToken: string,
  profile:
    | { id: string; name: string; email?: string; role: string; tenant_id?: string; workspaceId?: string }
    | undefined,
  email: string,
  explicitTenantId?: string,
): AuthUser {
  const claims = decodeJwtPayload(accessToken);
  const jwtRole = mapApiRole(pickPrimaryJwtRole(claims));
  const jwtTenant =
    ((claims?.tenant_id as string | undefined) ??
      (claims?.tenantId as string | undefined) ??
      '').trim();
  // SuperAdmin: optional "viewing" workspace (Users list) — separate from JWT/X-Tenant-Id auth tenant.
  const viewTenant =
    jwtRole === 'SuperAdmin' ? (readStoredViewTenantId() || '').trim() : '';

  if (profile?.id) {
    const fromProfile = toAuthUser(profile, email);
    const authTid =
      explicitTenantId?.trim() || fromProfile.workspaceId || readStoredTenantId() || jwtTenant || '';
    const workspaceId = viewTenant || authTid || fromProfile.workspaceId;
    // API `user.role` can lag JWT; take the higher privilege so SuperAdmin is not stuck as Editor.
    return { ...fromProfile, workspaceId, role: maxRole(fromProfile.role, jwtRole) };
  }
  const authTid =
    explicitTenantId?.trim() ||
    jwtTenant ||
    readStoredTenantId() ||
    tenantIdFromEnv() ||
    '';
  const roleStr = pickPrimaryJwtRole(claims);
  const sub = String(claims?.sub ?? claims?.userId ?? 'unknown');
  return {
    id:          sub,
    name:        email.includes('@') ? email.split('@')[0]! : email || 'User',
    email,
    role:        mapApiRole(roleStr || 'editor'),
    workspaceId: viewTenant || authTid,
  };
}

/** Normalize role strings from JWT / API (`super_admin`, `tenant_owner`, …). */
function normalizeRoleKey(role: string): string {
  return role.toLowerCase().replace(/[\s_-]+/g, '');
}

/**
 * When JWT `roles` is an array, choose the highest-privilege entry so `["editor","super_admin"]`
 * maps to SuperAdmin (fixes always landing as Editor on editorial).
 */
// spec §4: roles are SuperAdmin / Owner / Admin / Editor only — no Viewer.
// Unknown/legacy roles fall back to 'editor' (lowest valid workspace role).
function pickPrimaryJwtRole(claims: Record<string, unknown> | null): string {
  if (!claims) return 'editor';
  const list: string[] = [];
  const roles = claims.roles;
  const authorities = claims.authorities;
  if (Array.isArray(roles)) for (const r of roles) if (r != null) list.push(String(r));
  else if (Array.isArray(authorities)) for (const r of authorities) if (r != null) list.push(String(r));
  else if (claims?.role != null) list.push(String(claims.role));
  if (!list.length) return 'editor';
  const rank = (s: string) => {
    const n = normalizeRoleKey(s);
    if (n.includes('superadmin') || n === 'systemadmin' || n === 'platformadmin') return 100;
    if (n.includes('tenantowner') || n === 'owner') return 80;
    if (n.includes('tenantadmin') || n === 'admin') return 70;
    if (n.includes('tenanteditor') || n === 'editor') return 50;
    return 5; // unknown/legacy roles (including old 'viewer') → treated as editor
  };
  let best = list[0]!;
  let bestR = rank(best);
  for (const r of list) {
    const rr = rank(r);
    if (rr > bestR) {
      best = r;
      bestR = rr;
    }
  }
  return best;
}

export function mapApiRole(role: string): Role {
  const key = normalizeRoleKey(role);
  const table: Record<string, Role> = {
    superadmin:     'SuperAdmin',
    systemadmin:    'SuperAdmin',
    platformadmin:  'SuperAdmin',
    /** Workspace owner — distinct from tenant admin in UI and RBAC messaging. */
    owner:          'Owner',
    tenantowner:    'Owner',
    /** Tenant/workspace administrator (non-owner). */
    admin:          'Admin',
    tenantadmin:    'Admin',
    editor:         'Editor',
    tenanteditor:   'Editor',
    // 'viewer' not in spec — map to Editor (lowest valid workspace role)
    viewer:         'Editor',
  };
  return table[key] ?? 'Editor';
}

function toAuthUser(raw: {
  id: string;
  name: string;
  email?: string;
  role: string;
  tenant_id?: string;
  workspaceId?: string;
}, fallbackEmail: string): AuthUser {
  const workspaceId = raw.workspaceId ?? raw.tenant_id ?? '';
  return {
    id:          raw.id,
    name:        raw.name,
    email:       raw.email ?? fallbackEmail,
    role:        mapApiRole(raw.role),
    workspaceId,
  };
}

/** spec §4 / guardrails §1.3: Owner/Admin/Editor only. No Viewer. */
const PERMISSION_MATRIX: Record<Role, string[]> = {
  SuperAdmin: ['*'],
  Owner:      ['users:read','users:write','content:read','content:write','content:publish','media:read','media:write'],
  Admin:      ['users:read','users:write','content:read','content:write','content:publish','media:read','media:write'],
  Editor:     ['content:read','content:write','content:publish','media:read','media:write'],
};

/** Silent access-token refresh lead time — keep active sessions uninterrupted for ≥10 min. */
const SILENT_REFRESH_LEAD_SEC = 600;
/** Last-resort "session expiring" modal — only if silent refresh was delayed (e.g. tab sleep). */
const WARNING_LEAD_SEC = 120;

/** Restore `user` from JWT after refresh (F5); short/expired access token → user null until refresh runs. */
function loadSession(): { token: string | null; user: AuthUser | null } {
  const token = localStorage.getItem('auth_token');
  if (!token) return { token: null, user: null };
  if (!decodeJwtPayload(token)) return { token, user: null };
  const left = secondsUntilJwtExp(token);
  const skew = 45;
  if (left !== null && left <= skew) {
    return { token, user: null };
  }
  const claims = decodeJwtPayload(token);
  const email =
    localStorage.getItem(USER_EMAIL_STORAGE_KEY) ||
    (typeof claims?.email === 'string' ? claims.email : '') ||
    'user@local';
  try {
    return { token, user: userFromTokenAndOptionalProfile(token, undefined, email, readStoredTenantId()) };
  } catch {
    return { token, user: null };
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const initialSession = useMemo(() => loadSession(), []);
  const [user,  setUser]  = useState<AuthUser | null>(() => initialSession.user);
  const [token, setToken] = useState<string | null>(() => initialSession.token);
  const refreshTimer = useRef<ReturnType<typeof setTimeout>>();
  const warningTimer = useRef<ReturnType<typeof setTimeout>>();
  const countdownInterval = useRef<ReturnType<typeof setInterval>>();
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [warningSecondsRemaining, setWarningSecondsRemaining] = useState(WARNING_LEAD_SEC);
  const refreshTokenRef = useRef<() => Promise<string>>(async () => {
    throw new Error('refresh not ready');
  });
  const logoutRef = useRef<() => Promise<void>>(async () => {});

  const permissions = user ? (PERMISSION_MATRIX[user.role] ?? []) : [];

  const isAuthReady =
    !token || !!user || !localStorage.getItem(REFRESH_STORAGE_KEY);

  const scheduleRefresh = useCallback((expiresIn: number) => {
    // Clear existing timers
    clearTimeout(refreshTimer.current);
    clearTimeout(warningTimer.current);
    clearInterval(countdownInterval.current);
    setShowTimeoutWarning(false);

    // Silent refresh ~10 min before expiry so active users are not interrupted.
    // If already inside that window (e.g. tab woke late), refresh immediately.
    refreshTimer.current = setTimeout(async () => {
      try {
        await refreshTokenRef.current();
      } catch {
        await logoutRef.current();
      }
    }, Math.max((expiresIn - SILENT_REFRESH_LEAD_SEC) * 1000, 0));

    // Last-resort modal only in the final 2 minutes (tab sleep / missed silent refresh).
    // Normal path never shows this — silent refresh renews the token first.
    const warningDelayMs = Math.max((expiresIn - WARNING_LEAD_SEC) * 1000, 0);
    if (warningDelayMs > 0 && expiresIn > WARNING_LEAD_SEC) {
      warningTimer.current = setTimeout(() => {
        setWarningSecondsRemaining(WARNING_LEAD_SEC);
        setShowTimeoutWarning(true);
        countdownInterval.current = setInterval(() => {
          setWarningSecondsRemaining((prev) => {
            const next = prev - 1;
            if (next <= 0) {
              clearInterval(countdownInterval.current);
              return 0;
            }
            return next;
          });
        }, 1000);
      }, warningDelayMs);
    }
  }, []);

  const logout = useCallback(async () => {
    try { await apiClient.post('/auth/logout'); } catch { /* best effort */ }
    finally {
      clearTimeout(refreshTimer.current);
      clearTimeout(warningTimer.current);
      clearInterval(countdownInterval.current);
      setShowTimeoutWarning(false);
      setToken(null);
      setUser(null);
      localStorage.removeItem('auth_token');
      localStorage.removeItem(REFRESH_STORAGE_KEY);
      localStorage.removeItem(USER_EMAIL_STORAGE_KEY);
      clearTenantContext();
      clearWorkspaceTheme();
      clearRecoCountdownAnchor();
    }
  }, []);

  const extendSession = useCallback(async () => {
    setShowTimeoutWarning(false);
    clearInterval(countdownInterval.current);
    try {
      await refreshTokenRef.current();
    } catch (err) {
      console.error('[AuthContext] Failed to extend session:', err);
      await logoutRef.current();
    }
  }, []);

  const refreshToken = useCallback(async (): Promise<string> => {
    const storedRt = localStorage.getItem(REFRESH_STORAGE_KEY);
    if (!storedRt) throw new Error('No refresh token');
    const slug = readStoredWorkspaceSlug();
    const tid = readStoredTenantId();
    const body: { refreshToken: string; workspaceSlug?: string } = { refreshToken: storedRt };
    if (slug) body.workspaceSlug = slug;
    // Always send X-Tenant-Id when available so the BE can resolve tenant even if slug lookup fails
    const res = await apiClient.post<unknown>(
      '/auth/refresh',
      body,
      tid ? { headers: { 'X-Tenant-Id': tid } } : undefined,
    );
    const raw = unwrapApiBody<{
      access_token?: string;
      accessToken?: string;
      token?: string;
      refresh_token?: string;
      refreshToken?: string;
      expires_in?: number;
      expiresIn?: number;
      tenantId?: string;
      tenant_id?: string;
      workspaceSlug?: string | null;
      workspace_slug?: string | null;
    }>(res.data);
    const newToken = raw.accessToken ?? raw.access_token ?? raw.token;
    const expiresIn = raw.expiresIn ?? raw.expires_in;
    const nextRt = raw.refreshToken ?? raw.refresh_token;
    if (!newToken || expiresIn == null) throw new Error('Invalid refresh response');
    setToken(newToken);
    localStorage.setItem('auth_token', newToken);
    if (nextRt) localStorage.setItem(REFRESH_STORAGE_KEY, nextRt);
    const email = localStorage.getItem(USER_EMAIL_STORAGE_KEY) || 'user@local';
    const rtTenant =
      typeof raw.tenantId === 'string' && raw.tenantId.trim()
        ? raw.tenantId.trim()
        : typeof raw.tenant_id === 'string' && raw.tenant_id.trim()
          ? raw.tenant_id.trim()
          : undefined;
    const rtSlug = raw.workspaceSlug ?? raw.workspace_slug;
    if (rtTenant) {
      persistTenantContext(
        rtTenant,
        typeof rtSlug === 'string' && rtSlug.trim() ? rtSlug.trim() : rtSlug,
      );
    }
    setUser((prev) => {
      const u = userFromTokenAndOptionalProfile(newToken, undefined, email, rtTenant ?? readStoredTenantId());
      if (prev && prev.workspaceId === u.workspaceId && prev.workspace) {
        return { ...u, workspace: prev.workspace };
      }
      return u;
    });
    scheduleRefresh(expiresIn);
    return newToken;
  }, [scheduleRefresh]);

  useEffect(() => {
    refreshTokenRef.current = refreshToken;
  }, [refreshToken]);

  useEffect(() => {
    logoutRef.current = logout;
  }, [logout]);

  useEffect(() => {
    configureApiClient({
      getToken: () => token,
      refreshToken,
      getTenantId: () => {
        try {
          return localStorage.getItem(TENANT_ID_STORAGE_KEY)?.trim() || null;
        } catch {
          return null;
        }
      },
    });
  }, [token, refreshToken]);

  /** Access token near expiry or missing `user` — refresh; or repair `user` from JWT when still valid. */
  useEffect(() => {
    if (user || !token) return;
    const rt = localStorage.getItem(REFRESH_STORAGE_KEY);
    if (!rt) {
      localStorage.removeItem('auth_token');
      setToken(null);
      return;
    }
    const left = secondsUntilJwtExp(token);
    const email = localStorage.getItem(USER_EMAIL_STORAGE_KEY) || 'user@local';
    if (left !== null && left > 45) {
      try {
        setUser((prev) => {
          const u = userFromTokenAndOptionalProfile(token, undefined, email, readStoredTenantId());
          if (prev && prev.workspaceId === u.workspaceId && prev.workspace) {
            return { ...u, workspace: prev.workspace };
          }
          return u;
        });
        return;
      } catch {
        /* fall through to refresh */
      }
    }
    let cancelled = false;
    void (async () => {
      try {
        await refreshToken();
      } catch {
        if (!cancelled) await logout();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, user, refreshToken, logout]);

  /** After hydration, arm refresh timer from JWT `exp` when API did not tell us `expiresIn`. */
  useEffect(() => {
    if (!user || !token) return;
    const sec = secondsUntilJwtExp(token);
    if (sec != null && sec > 0) scheduleRefresh(sec);
  }, [user, token, scheduleRefresh]);

  /** Recover from browser tab suspension: timers can fire late when tabs are backgrounded.
   * On visibility restore, check if the token is near expiry and refresh proactively. */
  useEffect(() => {
    if (!token) return;
    const handleVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const left = secondsUntilJwtExp(token);
      if (left !== null && left < SILENT_REFRESH_LEAD_SEC) {
        void refreshTokenRef.current().catch(() => void logoutRef.current());
      }
    };
    document.addEventListener('visibilitychange', handleVisible);
    return () => document.removeEventListener('visibilitychange', handleVisible);
  }, [token]);

  /** SP-027 — theme tokens + shell branding from `GET /workspaces` when tenant or session changes. */
  useEffect(() => {
    const tid = user?.workspaceId?.trim();
    if (!tid || !token) return;
    let cancelled = false;
    void (async () => {
      const wb = await fetchAndApplyWorkspaceBranding(tid);
      if (cancelled) return;
      setUser((prev) => {
        if (!prev || prev.workspaceId !== tid) return prev;
        if (!wb) return prev;
        return { ...prev, workspace: wb };
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.workspaceId, token]);

  const establishSession = useCallback((
    session: SessionTokens,
    profile?: { id: string; name: string; email?: string; role: string; tenant_id?: string; workspaceId?: string },
  ): AuthUser => {
    const email = session.email.trim().toLowerCase();
    const t = session.accessToken;
    if (!t || !email) throw new Error('Invalid session payload');
    const loginTenant =
      typeof session.tenantId === 'string' && session.tenantId.trim()
        ? session.tenantId.trim()
        : undefined;
    const loginSlug = session.workspaceSlug;
    if (loginTenant) {
      persistTenantContext(
        loginTenant,
        typeof loginSlug === 'string' && loginSlug.trim() ? loginSlug.trim() : loginSlug,
      );
    }
    const u = userFromTokenAndOptionalProfile(t, profile, email, loginTenant);
    setToken(t);
    setUser(u);
    localStorage.setItem('auth_token', t);
    localStorage.setItem(USER_EMAIL_STORAGE_KEY, email);
    if (session.refreshToken) localStorage.setItem(REFRESH_STORAGE_KEY, session.refreshToken);
    const expiresIn = session.expiresIn ?? secondsUntilJwtExp(t) ?? 3600;
    scheduleRefresh(expiresIn);
    return u;
  }, [scheduleRefresh]);

  const login = useCallback(async (email: string, password: string, _options?: LoginOptions): Promise<AuthUser> => {
    // Workspace-free login: server auto-resolves tenant by email.
    // workspaceSlug / loginTenantId options are no longer sent to the API.
    const res = await apiClient.post<unknown>('/auth/login', { email, password });
    const raw = unwrapApiBody<{
      access_token?: string;
      accessToken?: string;
      token?: string;
      refresh_token?: string;
      refreshToken?: string;
      expires_in?: number;
      expiresIn?: number;
      tenantId?: string;
      tenant_id?: string;
      workspaceSlug?: string | null;
      workspace_slug?: string | null;
      user?: { id: string; name: string; email?: string; role: string; tenant_id?: string; workspaceId?: string };
    }>(res.data);
    const t = raw.accessToken ?? raw.access_token ?? raw.token;
    const expiresIn = raw.expiresIn ?? raw.expires_in;
    const rt = raw.refreshToken ?? raw.refresh_token;
    if (!t || expiresIn == null) throw new Error('Invalid login response');
    const loginTenant =
      typeof raw.tenantId === 'string' && raw.tenantId.trim()
        ? raw.tenantId.trim()
        : typeof raw.tenant_id === 'string' && raw.tenant_id.trim()
          ? raw.tenant_id.trim()
          : undefined;
    const loginSlug = raw.workspaceSlug ?? raw.workspace_slug;
    return establishSession(
      {
        accessToken: t,
        refreshToken: rt,
        expiresIn,
        email,
        tenantId: loginTenant,
        workspaceSlug: typeof loginSlug === 'string' ? loginSlug : loginSlug ?? null,
      },
      raw.user,
    );
  }, [establishSession]);

  const switchWorkspace = useCallback(async (workspaceId: string) => {
    const tid = workspaceId.trim();
    if (!tid) return;

    // SuperAdmin header switcher: UI viewing context only (Users list uses ?tenantId=).
    // Do NOT call /workspace/:id/permissions — that API requires workspaceId === JWT tenant.
    // Do NOT overwrite auth_tenant_id — X-Tenant-Id must keep matching the JWT or all APIs 401.
    setUser((u) => {
      if (!u) return u;
      if (u.role === 'SuperAdmin') {
        writeStoredViewTenantId(tid);
        return { ...u, workspaceId: tid };
      }
      // Non–SuperAdmin: keep JWT-aligned tenant id in storage (switcher is SuperAdmin-gated).
      try {
        localStorage.setItem(TENANT_ID_STORAGE_KEY, tid);
      } catch {
        /* ignore */
      }
      return { ...u, workspaceId: tid };
    });
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthReady,
        isAuthenticated: !!token && !!user,
        permissions,
        login,
        establishSession,
        logout,
        refreshToken,
        switchWorkspace,
        timeoutWarning: {
          show: showTimeoutWarning,
          secondsRemaining: warningSecondsRemaining,
        },
        extendSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}

export function usePermissions(action: string): boolean {
  const { permissions } = useAuth();
  return permissions.includes('*') || permissions.includes(action);
}
